import { useEffect, useId, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useController, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import type {
  CatalogHardwareListItem,
  CatalogLightingListItem,
  EdgeListItem,
  MaterialCategoryValue,
  MaterialListItem,
  MaterialUsageScopeValue,
} from '@kreart/shared';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  materialsApi,
  edgesApi,
  hardwareApi,
  lightingApi,
} from '@/features/catalog/components/admin/api';
import { usePricingConstants } from '@/features/pricing/hooks';
import { specialMaterialFlags } from '@/features/pricing/specialMaterialFlags';
import { cn } from '@/lib/cn';

interface BaseProps<TItem> {
  name: string;
  label: string;
  placeholder?: string;
  required?: boolean;
  emptyOptionLabel?: string;
  emptyOptionDescription?: string;
  onSelectedOption?: (item: TItem | null) => void;
  /**
   * The optional "none" row was chosen — a deliberate "this piece takes no
   * edge", as opposed to a cleared field.
   *
   * `onSelectedOption` cannot tell the two apart: typing to search also drops
   * the bound id and reports null, so a consumer that acted on every null would
   * fire the moment someone started retyping an edge. This one runs only on the
   * row itself.
   */
  onEmptySelected?: () => void;
  /**
   * Optional per-item advisories. Each one shows a short `badge` next to that
   * row in the dropdown and, once the row is the selected value, renders its
   * `message` as a line under the input.
   *
   * Used to flag unpriced materials (price 0) so a silent 0 total is caught at
   * select time, and to say how a special-pricing material changes the price in
   * the slot being filled. Return an empty array for rows with nothing to flag.
   *
   * A list rather than a single flag because the two overlap: a glass-sanded
   * board can also be the one nobody has priced yet, and dropping either notice
   * to make room for the other is the wrong trade.
   */
  flagFor?: (item: TItem) => CatalogItemFlag[];
  /**
   * Let the open list outgrow the input it hangs off.
   *
   * The panel is as wide as its field and five rows tall, which is right where
   * the field is a whole form row. In a narrow grid column — a cut-list row's
   * material picker is a fraction of the dialog — that truncates every option to
   * a few characters and shows a handful of them, so the list is least readable
   * exactly where the input gives you the least to read. Set this there: the
   * panel keeps the input's left edge, grows right to a legible width, and shows
   * roughly twice as many rows before it starts scrolling.
   */
  roomyList?: boolean;
}

/** One advisory line under a picker, plus its badge in the dropdown. */
export interface CatalogItemFlag {
  badge: string;
  message: string;
  /**
   * 'warning' — something is off and the price will be wrong or surprising.
   * 'info'    — a rule worth knowing about, working as intended.
   * Defaults to 'warning', which is what the only pre-existing flag was.
   */
  tone?: 'warning' | 'info';
}

interface SuggestOption<TItem> {
  id: string;
  label: string;
  description?: string;
  item: TItem;
}

const CATEGORY_PREFIX: Record<string, string> = {
  PAL_CAT_1: 'PAL',
  PAL_CAT_2: 'PAL',
  PAL_CAT_3: 'PAL',
  MDF_VOPSIT: 'MDF',
  STICLA: 'Sticla',
  BLAT_TERMOREZISTENT: 'Blat',
  PFL: 'PFL',
};

/**
 * One line that names a board the way every picker in the app does. Exported
 * for the clone dialog's material section, which labels boards from a slimmer
 * payload — hence the structural pick rather than the full list item.
 */
export function materialLabel(
  m: Pick<MaterialListItem, 'category' | 'name' | 'sku' | 'structure' | 'thicknessMm'>,
): string {
  const prefix = CATEGORY_PREFIX[m.category];
  const name =
    prefix && !m.name.toUpperCase().startsWith(prefix.toUpperCase())
      ? `${prefix} ${m.name}`
      : m.name;
  return [name, m.sku, m.structure, m.thicknessMm ? `${m.thicknessMm}mm` : null]
    .filter(Boolean)
    .join(' ');
}

function edgeLabel(e: EdgeListItem): string {
  return [
    e.name,
    e.sku ? `SKU ${e.sku}` : null,
    e.thicknessMm ? `${e.thicknessMm}mm` : null,
    e.manufacturer,
  ]
    .filter(Boolean)
    .join(' — ');
}

function hardwareLabel(h: CatalogHardwareListItem): string {
  return [h.name, h.manufacturer, h.sku].filter(Boolean).join(' ');
}

function lightingLabel(l: CatalogLightingListItem): string {
  return [
    l.name,
    l.internalCode ? `Cod ${l.internalCode}` : null,
    l.sku ? `SKU ${l.sku}` : null,
    l.manufacturer,
  ]
    .filter(Boolean)
    .join(' — ');
}

function CatalogAutosuggest<TItem>({
  name,
  label,
  placeholder,
  required,
  queryKey,
  queryFn,
  getById,
  toOption,
  emptyOptionLabel,
  emptyOptionDescription,
  onSelectedOption,
  onEmptySelected,
  flagFor,
  roomyList,
}: BaseProps<TItem> & {
  queryKey: (search: string) => readonly unknown[];
  queryFn: (search: string) => Promise<TItem[]>;
  getById?: (id: string) => Promise<TItem>;
  toOption: (item: TItem) => SuggestOption<TItem>;
}) {
  const { t } = useTranslation();
  const listId = useId();
  const [search, setSearch] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  // Which row the arrow keys are on, as an index into `navigable` below. Reset
  // whenever the list opens or the options change, so it never points at a row
  // that has scrolled out of the result set.
  const [activeIndex, setActiveIndex] = useState(0);
  const {
    field,
    fieldState: { error },
  } = useController({ name });
  const { clearErrors } = useFormContext();
  const trimmed = search.trim();
  const query = useQuery({
    queryKey: queryKey(trimmed),
    queryFn: () => queryFn(trimmed),
    meta: { silent: true },
    staleTime: 60_000,
  });
  const options = useMemo(() => (query.data ?? []).map(toOption), [query.data, toOption]);
  const selectedFromOptions = options.find((opt) => opt.id === field.value) ?? null;
  const selectedQuery = useQuery({
    queryKey: [...queryKey(''), 'selected', field.value],
    queryFn: () => getById!((field.value ?? '') as string),
    enabled: Boolean(getById && field.value && !selectedFromOptions),
    meta: { silent: true },
    staleTime: 60_000,
  });
  const selected =
    selectedFromOptions ?? (selectedQuery.data ? toOption(selectedQuery.data) : null);
  const selectedFlags = selected ? (flagFor?.(selected.item) ?? []) : [];
  const errorId = `${listId}-error`;
  const listboxId = `${listId}-options`;

  /**
   * Everything the arrows can land on, in the order it is drawn.
   *
   * The optional "no material" row is a real choice, not a header, so it takes
   * part — otherwise the first Down would skip past it.
   */
  const navigable = useMemo(
    () => [
      ...(emptyOptionLabel ? [{ kind: 'empty' as const }] : []),
      ...options.map((option) => ({ kind: 'option' as const, option })),
    ],
    [emptyOptionLabel, options],
  );
  const activeId = `${listboxId}-${activeIndex}`;

  useEffect(() => {
    setActiveIndex(0);
  }, [open, trimmed]);

  useEffect(() => {
    if (!field.value && selectedId) {
      setSelectedId(null);
      setSearch('');
    }
  }, [field.value, selectedId]);

  useEffect(() => {
    if (field.value && field.value !== selectedId && search) {
      setSearch('');
    }
  }, [field.value, search, selectedId]);

  const handleSelect = (option: SuggestOption<TItem>) => {
    setSearch(option.label);
    setSelectedId(option.id);
    field.onChange(option.id);
    // Picking a row IS the answer to "Choose a value from the suggestions", so
    // the message must go with it.
    //
    // `field.onChange` alone does not re-validate: the forms here run RHF's
    // default `mode: 'onSubmit'`, and `reValidateMode: 'onChange'` only starts
    // applying once `isSubmitted` is true. An error raised by a bare `trigger()`
    // — as the cabinet dialog does before generating a cut list — therefore
    // stayed pinned under a field the user had just filled in correctly, with no
    // way to clear it short of submitting.
    //
    // Safe to clear outright: the only rules on these id fields are "a value is
    // required", which selecting one satisfies, and submit re-validates
    // everything anyway.
    clearErrors(name);
    onSelectedOption?.(option.item);
    setOpen(false);
  };

  const handleEmptySelect = () => {
    setSearch('');
    setSelectedId(null);
    field.onChange('');
    onSelectedOption?.(null);
    onEmptySelected?.();
    setOpen(false);
  };

  const handleSearchChange = (next: string) => {
    setSearch(next);
    setOpen(true);
    if (field.value || selectedId) {
      setSelectedId(null);
      field.onChange('');
      onSelectedOption?.(null);
    }
  };

  return (
    <div>
      <Label htmlFor={listId}>
        {label}
        {required ? ' *' : ''}
      </Label>
      <div className="relative">
        <Input
          id={listId}
          value={
            search || selected?.label || (!field.value && !open ? (emptyOptionLabel ?? '') : '')
          }
          placeholder={placeholder}
          role="combobox"
          aria-autocomplete="list"
          aria-expanded={open}
          aria-controls={listboxId}
          aria-invalid={error ? true : undefined}
          aria-describedby={error ? errorId : undefined}
          onFocus={() => setOpen(true)}
          onBlur={() => {
            field.onBlur();
            window.setTimeout(() => setOpen(false), 100);
          }}
          aria-activedescendant={open ? activeId : undefined}
          onKeyDown={(event) => {
            if (event.key === 'Escape') {
              setOpen(false);
              return;
            }
            if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
              event.preventDefault();
              // Down also opens a closed list, which is what every other
              // combobox on the platform does.
              if (!open) {
                setOpen(true);
                return;
              }
              if (navigable.length === 0) return;
              const step = event.key === 'ArrowDown' ? 1 : -1;
              // Wraps, so holding one arrow cycles rather than sticking at an end.
              setActiveIndex((current) => (current + step + navigable.length) % navigable.length);
              return;
            }
            if (event.key === 'Enter' && open) {
              const picked = navigable[activeIndex];
              if (!picked) return;
              // Never submits the surrounding form: the keystroke chose a row.
              event.preventDefault();
              if (picked.kind === 'empty') handleEmptySelect();
              else handleSelect(picked.option);
            }
          }}
          onChange={(event) => handleSearchChange(event.currentTarget.value)}
        />
        {open && (
          <div
            id={listboxId}
            role="listbox"
            className={cn(
              'absolute z-[60] mt-1 w-full overflow-y-auto rounded-md border border-border bg-popover py-1 text-popover-foreground shadow-lg',
              // Capped against the viewport as well as in rem: the panel is only
              // ever wider than its field, never wider than the screen holding
              // the dialog.
              roomyList ? 'max-h-96 min-w-[min(28rem,90vw)]' : 'max-h-56',
            )}
          >
            {query.isFetching ? (
              <div className="px-3 py-2 text-sm text-muted-foreground">
                {t('pricing.searching')}
              </div>
            ) : emptyOptionLabel || options.length > 0 ? (
              <>
                {emptyOptionLabel && (
                  <button
                    type="button"
                    role="option"
                    id={`${listboxId}-0`}
                    aria-selected={activeIndex === 0}
                    onMouseEnter={() => setActiveIndex(0)}
                    className={cn(
                      'block w-full px-3 py-2 text-left text-sm hover:bg-accent hover:text-accent-foreground',
                      !field.value && 'bg-accent text-accent-foreground',
                      activeIndex === 0 && 'bg-accent text-accent-foreground',
                    )}
                    onMouseDown={(event) => {
                      event.preventDefault();
                      handleEmptySelect();
                    }}
                  >
                    <span className="block truncate font-medium">{emptyOptionLabel}</span>
                    {emptyOptionDescription && (
                      <span className="block truncate text-xs text-muted-foreground">
                        {emptyOptionDescription}
                      </span>
                    )}
                  </button>
                )}
                {options.map((opt, optIndex) => {
                  const optFlags = flagFor?.(opt.item) ?? [];
                  // Offset by the "no material" row when it is shown, so the
                  // index lines up with `navigable`.
                  const index = optIndex + (emptyOptionLabel ? 1 : 0);
                  return (
                    <button
                      key={opt.id}
                      type="button"
                      role="option"
                      id={`${listboxId}-${index}`}
                      aria-selected={index === activeIndex}
                      onMouseEnter={() => setActiveIndex(index)}
                      className={cn(
                        'block w-full px-3 py-2 text-left text-sm hover:bg-accent hover:text-accent-foreground',
                        field.value === opt.id && 'bg-accent text-accent-foreground',
                        index === activeIndex && 'bg-accent text-accent-foreground',
                      )}
                      onMouseDown={(event) => {
                        event.preventDefault();
                        handleSelect(opt);
                      }}
                    >
                      <span className="flex items-center gap-2">
                        <span className="min-w-0 flex-1 truncate font-medium">{opt.label}</span>
                        {optFlags.map((flag, flagIndex) => (
                          <span
                            // Index keys: the list is re-derived whole on every
                            // render and its order is fixed by flagFor, and two
                            // flags can legitimately carry the same badge text.
                            key={flagIndex}
                            className={cn(
                              'shrink-0 rounded-md px-1.5 py-0.5 text-[10px] font-medium',
                              flag.tone === 'info'
                                ? 'border border-border bg-muted text-muted-foreground'
                                : 'bg-warning/15 text-warning',
                            )}
                          >
                            {flag.badge}
                          </span>
                        ))}
                      </span>
                      {opt.description && (
                        <span className="block truncate text-xs text-muted-foreground">
                          {opt.description}
                        </span>
                      )}
                    </button>
                  );
                })}
              </>
            ) : (
              <div className="px-3 py-2 text-sm text-muted-foreground">
                {t('common.emptyState.title')}
              </div>
            )}
          </div>
        )}
      </div>
      <input
        type="hidden"
        name={field.name}
        value={(field.value ?? '') as string}
        ref={field.ref}
        readOnly
      />
      <p className="mt-1 text-xs text-muted-foreground">
        {query.isFetching ? t('pricing.searching') : t('pricing.pickFromSuggestions')}
      </p>
      {selectedFlags.map((flag, flagIndex) => (
        <p
          key={flagIndex}
          className={cn(
            'mt-1 text-xs font-medium',
            flag.tone === 'info' ? 'text-muted-foreground' : 'text-warning',
          )}
          role="status"
        >
          {flag.message}
        </p>
      ))}
      {error && (
        <p id={errorId} className="mt-1 text-xs text-destructive" role="alert">
          {error.message}
        </p>
      )}
    </div>
  );
}

/** A material is "unpriced" when pricePerM2 is missing or parses to 0 or less. */
function isUnpricedMaterial(m: MaterialListItem): boolean {
  const raw = m.pricePerM2;
  if (raw === undefined || raw === null || raw === '') return true;
  const value = Number.parseFloat(raw);
  return !Number.isFinite(value) || value <= 0;
}

export function MaterialAutosuggest({
  category,
  usageScope,
  excludeCategories,
  ...props
}: BaseProps<MaterialListItem> & {
  category?: MaterialCategoryValue;
  usageScope?: MaterialUsageScopeValue;
  excludeCategories?: MaterialCategoryValue[];
}) {
  const { t } = useTranslation();
  // One cached request for the whole session, shared by every picker on screen.
  const { data: constants } = usePricingConstants();
  const excluded = excludeCategories ? new Set(excludeCategories) : null;
  return (
    <CatalogAutosuggest
      {...props}
      // Special-pricing rules follow the material, not the slot, so every
      // material picker in the app explains them — cabinet, panel, countertop
      // and drawer alike.
      flagFor={(item) => [
        ...(isUnpricedMaterial(item)
          ? [{ badge: t('pricing.noPriceBadge'), message: t('pricing.noPriceWarning') }]
          : []),
        ...specialMaterialFlags(item, constants?.glassSandingMarkup, t),
      ]}
      queryKey={(search) => [
        'catalog-materials',
        'suggest',
        { active: true, category, usageScope, excludeCategories, search },
      ]}
      queryFn={(search) =>
        materialsApi
          .list({
            page: 1,
            pageSize: excluded ? 100 : 20,
            active: true,
            includeDeleted: false,
            category,
            usageScope,
            search: search || undefined,
          })
          .then((res) =>
            excluded ? res.items.filter((m) => !excluded.has(m.category)) : res.items,
          )
      }
      getById={materialsApi.get}
      toOption={(item) => ({
        id: item.id,
        label: materialLabel(item),
        item,
      })}
    />
  );
}

export function EdgeAutosuggest(props: BaseProps<EdgeListItem>) {
  return (
    <CatalogAutosuggest
      {...props}
      queryKey={(search) => ['catalog-edges', 'suggest', { active: true, search }]}
      queryFn={(search) =>
        edgesApi
          .list({
            page: 1,
            pageSize: 20,
            active: true,
            includeDeleted: false,
            search: search || undefined,
          })
          .then((res) => res.items)
      }
      getById={edgesApi.get}
      toOption={(item) => ({
        id: item.id,
        label: edgeLabel(item),
        description: item.eligibleStructures.join(', '),
        item,
      })}
    />
  );
}

export function HardwareAutosuggest(props: BaseProps<CatalogHardwareListItem>) {
  return (
    <CatalogAutosuggest
      {...props}
      queryKey={(search) => ['catalog-hardware', 'suggest', { active: true, search }]}
      queryFn={(search) =>
        hardwareApi
          .list({
            page: 1,
            pageSize: 20,
            active: true,
            includeDeleted: false,
            search: search || undefined,
          })
          .then((res) => res.items)
      }
      getById={hardwareApi.get}
      toOption={(item) => ({
        id: item.id,
        label: hardwareLabel(item),
        item,
      })}
    />
  );
}

export function LightingAutosuggest(props: BaseProps<CatalogLightingListItem>) {
  return (
    <CatalogAutosuggest
      {...props}
      queryKey={(search) => ['catalog-lighting', 'suggest', { active: true, search }]}
      queryFn={(search) =>
        lightingApi
          .list({
            page: 1,
            pageSize: 20,
            active: true,
            includeDeleted: false,
            search: search || undefined,
          })
          .then((res) => res.items)
      }
      getById={lightingApi.get}
      toOption={(item) => ({
        id: item.id,
        label: lightingLabel(item),
        description:
          item.pricePerPiece !== undefined
            ? `${item.pricePerPiece} ${item.currency}`
            : item.currency,
        item,
      })}
    />
  );
}
